import { useCallback, useEffect, useState } from 'react';

import { type InfiniteData, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '@splitfiesta/core';
import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import type {
  ActivityFeedItemDto,
  ExpenseResponseDto,
} from '@splitfiesta/shared';

import {
  useActivityFeedInfiniteQuery,
  useGroupActivityFeedInfiniteQuery,
} from '@/hooks/queries/useActivity';
import {
  useAddCommentMutation,
  useToggleReactionByIdMutation,
  useUpdateCommentMutation,
} from '@/hooks/queries/useExpenses';
import { useEffectiveUserId } from '@/hooks/useEffectiveUserId';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { useReactionOptimisticUpdates } from '@/hooks/useReactionOptimisticUpdates';
import { useTypedApi } from '@/hooks/useTypedApi';

import { activityFeedService } from '@/services/activity/ActivityFeedService';
import { activityInteractionService } from '@/services/activity/ActivityInteractionService';

import { useAuthStore } from '@/stores/authStore';
import { useActivityFilter, useSetActivityFilter } from '@/stores/uiStore';

import { toast } from '@/utils/toast';

import { ActivityFeedPresentation } from './ActivityFeedPresentation';

type ActivityFeedPage = {
  activities: ActivityFeedItemDto[];
  hasMore: boolean;
  nextCursor?: string;
};

type ActivityFeedInfiniteData = InfiniteData<ActivityFeedPage>;

const FEED_PARAMS = { limit: 25 };

interface ActivityFeedContainerProps {
  groupId?: string;
  showTitle?: boolean;
}

/**
 * Container component for ActivityFeed
 * Manages state, data fetching, and business logic
 * Can be filtered by groupId for group-specific feeds
 */
export function ActivityFeedContainer({
  groupId,
  showTitle = true,
}: ActivityFeedContainerProps = {}) {
  const { t } = useTranslation();
  useNamespace('common');

  const user = useAuthStore(state => state.user);
  const effectiveUserId = useEffectiveUserId();
  const api = useTypedApi();
  const queryClient = useQueryClient();

  // Activity filter settings
  const filterMode = useActivityFilter();
  const setActivityFilter = useSetActivityFilter();

  // State management
  const [activeExpenseId, setActiveExpenseId] = useState<string | null>(null);
  const [expandedComments, setExpandedComments] = useState<Set<string>>(
    new Set()
  );
  const [loadingComments, setLoadingComments] = useState<{
    [key: string]: boolean;
  }>({});
  const [loadingReaction] = useState<{
    [key: string]: boolean;
  }>({});

  // Fetch activity feed
  // If groupId is provided, use the new group-specific endpoint (complete history)
  // Otherwise, use the global activity feed (90-day TTL, multi-group)
  // Both hooks are called unconditionally to follow React's rules of hooks
  const globalFeedQuery = useActivityFeedInfiniteQuery(FEED_PARAMS, !groupId);
  const groupFeedQuery = useGroupActivityFeedInfiniteQuery(
    groupId ?? '',
    FEED_PARAMS,
    !!groupId
  );

  // Select the appropriate query based on whether groupId is provided
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading: initialLoading,
    // The optimistic writes must land on the key the feed reads.
    queryKey: feedQueryKey,
  } = groupId ? groupFeedQuery : globalFeedQuery;

  // Process activities using service
  const allActivities = activityFeedService.processPaginatedActivities(
    data?.pages || []
  );

  // Filter activities based on user participation
  const activities =
    filterMode === 'all'
      ? allActivities
      : allActivities.filter(activity => {
          if (activity.type === 'group_event') return false;
          if (!effectiveUserId) return true;
          if (filterMode === 'paidByMe') {
            return (
              activity.paidBy?.some(p => p.userId === effectiveUserId) ?? false
            );
          }
          // 'involved': creator OR payer OR split
          if (activity.userId === effectiveUserId) return true;
          if (activity.paidBy?.some(p => p.userId === effectiveUserId))
            return true;
          if (activity.splits?.some(s => s.userId === effectiveUserId))
            return true;
          return false;
        });

  // Auto-load all pages when a filter is active so client-side filtering
  // sees every activity, not just the first page.
  useEffect(() => {
    // Joins rather than restarts a page the empty-page auto-fetch already asked for.
    if (filterMode !== 'all' && hasNextPage && !isFetching && !initialLoading) {
      fetchNextPage({ cancelRefetch: false });
    }
  }, [filterMode, hasNextPage, isFetching, initialLoading, fetchNextPage]);

  // Optimistic updates hook
  const { addReactionOptimistically, toggleReactionOptimistically } =
    useReactionOptimisticUpdates({
      currentUserId: user?.id,
      currentUserName: user?.name,
    });

  // Mutations - use mutation that accepts expenseId at call time to avoid stale closure issues
  const toggleReactionMutation = useToggleReactionByIdMutation();
  const addCommentMutation = useAddCommentMutation(activeExpenseId || '');
  const updateCommentMutation = useUpdateCommentMutation();

  // Load more handler
  const loadMore = useCallback(() => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, isFetchingNextPage, hasNextPage]);

  // Infinite scroll ref
  const lastActivityRef = useInfiniteScroll({
    loading: isFetchingNextPage,
    hasMore: hasNextPage || false,
    onLoadMore: loadMore,
    enabled: true, // Always enable infinite scrolling
  });

  // Handle add reaction
  const handleAddReaction = async (activityId: string, emoji: string) => {
    // Optimistic update FIRST - use correct query key based on feed type
    const previousData = queryClient.getQueryData(feedQueryKey);

    queryClient.setQueryData(
      feedQueryKey,
      (oldData: ActivityFeedInfiniteData | undefined) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          pages: oldData.pages.map((page: ActivityFeedPage) => ({
            ...page,
            activities: page.activities.map((activity: ActivityFeedItemDto) => {
              if (activity.id !== activityId) return activity;

              const updatedReactions = addReactionOptimistically(
                activity.reactions || [],
                emoji
              );

              return {
                ...activity,
                reactions: updatedReactions,
              };
            }),
          })),
        };
      }
    );

    setActiveExpenseId(activityId);
    toggleReactionMutation.mutate(
      { expenseId: activityId, emoji, hasReacted: false },
      {
        onError: () => {
          queryClient.setQueryData(feedQueryKey, previousData);
        },
      }
    );
  };

  // Handle toggle reaction
  const handleToggleReaction = async (
    activityId: string,
    emoji: string,
    hasReacted: boolean
  ) => {
    // Optimistic update FIRST
    const previousData = queryClient.getQueryData(feedQueryKey);

    queryClient.setQueryData(
      feedQueryKey,
      (oldData: ActivityFeedInfiniteData | undefined) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          pages: oldData.pages.map((page: ActivityFeedPage) => ({
            ...page,
            activities: page.activities.map((activity: ActivityFeedItemDto) => {
              if (activity.id !== activityId) return activity;

              const updatedReactions = toggleReactionOptimistically(
                activity.reactions || [],
                emoji,
                hasReacted
              );

              return {
                ...activity,
                reactions: updatedReactions,
              };
            }),
          })),
        };
      }
    );

    setActiveExpenseId(activityId);
    toggleReactionMutation.mutate(
      { expenseId: activityId, emoji, hasReacted },
      {
        onError: () => {
          queryClient.setQueryData(feedQueryKey, previousData);
        },
      }
    );
  };

  // Handle submit comment
  const handleSubmitComment = async (activityId: string, text: string) => {
    if (!user) return;

    // Validate comment
    const validation = activityInteractionService.validateComment(text);
    if (!validation.isValid) {
      return;
    }

    const previousData = queryClient.getQueryData(feedQueryKey);

    // Create optimistic comment
    const optimisticComment =
      activityInteractionService.createOptimisticComment(
        validation.sanitizedText || text,
        {
          id: user.id,
          name: user.name || 'You',
          profileImage: user.profileImage ?? undefined,
        }
      );

    try {
      const currentActivity = activities.find(a => a.id === activityId);
      const needsFullComments = currentActivity?.hasMoreComments;

      let allExistingComments = currentActivity?.comments || [];

      if (needsFullComments) {
        try {
          setActiveExpenseId(activityId);
          const expenseData = await queryClient.fetchQuery({
            queryKey: queryKeys.expense(activityId),
            queryFn: async () => {
              const response = await api.expenses.getById(activityId);
              return response.data;
            },
          });
          const responseData = expenseData as ExpenseResponseDto;

          if (responseData?.comments) {
            allExistingComments = responseData.comments.map(c => ({
              id: c.id,
              user: c.name || 'Unknown',
              userId: c.userId,
              profileImage: c.profileImage,
              text: c.comment || '',
              createdAt: c.createdAt || new Date().toISOString(),
              updatedAt: c.updatedAt || c.createdAt || new Date().toISOString(),
              isEdited: c.isEdited,
            }));
          }
        } catch {
          // Failed to load existing comments, continue with what we have
        }
      }

      const totalCommentsAfterAdd = allExistingComments.length + 1;
      if (totalCommentsAfterAdd > 2) {
        setExpandedComments(prev => new Set(prev).add(activityId));
      }

      // Update cache optimistically
      queryClient.setQueryData(
        feedQueryKey,
        (oldData: ActivityFeedInfiniteData | undefined) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page: ActivityFeedPage) => ({
              ...page,
              activities: page.activities.map(
                (activity: ActivityFeedItemDto) => {
                  if (activity.id !== activityId) return activity;

                  const updatedComments = [
                    ...allExistingComments,
                    {
                      id: optimisticComment.id,
                      user: optimisticComment.name,
                      userId: optimisticComment.userId,
                      profileImage: optimisticComment.profileImage,
                      text: optimisticComment.comment,
                      createdAt: optimisticComment.createdAt,
                      updatedAt: optimisticComment.updatedAt,
                    },
                  ];

                  return {
                    ...activity,
                    comments: updatedComments,
                    commentCount: updatedComments.length,
                    hasMoreComments: false,
                  };
                }
              ),
            })),
          };
        }
      );

      setActiveExpenseId(activityId);
      await addCommentMutation.mutateAsync(text);

      // Fetch the expense to get comments with profile images
      const expenseData = await queryClient.fetchQuery({
        queryKey: queryKeys.expense(activityId),
        queryFn: async () => {
          const response = await api.expenses.getById(activityId);
          return response.data;
        },
      });
      const responseData = expenseData as ExpenseResponseDto;

      // Update cache with comments from expense (which have profileImage)
      if (responseData?.comments) {
        queryClient.setQueryData(
          feedQueryKey,
          (oldData: ActivityFeedInfiniteData | undefined) => {
            if (!oldData) return oldData;

            return {
              ...oldData,
              pages: oldData.pages.map((page: ActivityFeedPage) => ({
                ...page,
                activities: page.activities.map(
                  (activity: ActivityFeedItemDto) => {
                    if (activity.id !== activityId) return activity;

                    const updatedComments = responseData.comments!.map(c => ({
                      id: c.id,
                      user: c.name || 'Unknown',
                      userId: c.userId,
                      profileImage: c.profileImage,
                      text: c.comment || '',
                      createdAt: c.createdAt || new Date().toISOString(),
                      updatedAt:
                        c.updatedAt || c.createdAt || new Date().toISOString(),
                      isEdited: c.isEdited,
                    }));

                    return {
                      ...activity,
                      comments: updatedComments,
                      commentCount: updatedComments.length,
                      hasMoreComments: false,
                    };
                  }
                ),
              })),
            };
          }
        );
      }
    } catch {
      queryClient.setQueryData(feedQueryKey, previousData);
      toast.error(t('common:comments.submitError'));
    }
  };

  // Handle load all comments
  const handleLoadAllComments = async (activityId: string) => {
    if (loadingComments[activityId]) return;

    setLoadingComments(prev => ({ ...prev, [activityId]: true }));

    try {
      setActiveExpenseId(activityId);
      const expenseData = await queryClient.fetchQuery({
        queryKey: queryKeys.expense(activityId),
        queryFn: async () => {
          const response = await api.expenses.getById(activityId);
          return response.data;
        },
      });
      const responseData = expenseData as ExpenseResponseDto;

      if (responseData && responseData.comments) {
        setExpandedComments(prev => new Set(prev).add(activityId));

        queryClient.setQueryData(
          feedQueryKey,
          (oldData: ActivityFeedInfiniteData | undefined) => {
            if (!oldData) return oldData;

            return {
              ...oldData,
              pages: oldData.pages.map((page: ActivityFeedPage) => ({
                ...page,
                activities: page.activities.map(
                  (activity: ActivityFeedItemDto) => {
                    if (activity.id !== activityId) return activity;

                    const allComments =
                      responseData.comments?.map(c => ({
                        id: c.id,
                        user: c.name || 'Unknown',
                        userId: c.userId,
                        profileImage: c.profileImage,
                        text: c.comment || '',
                        createdAt: c.createdAt || new Date().toISOString(),
                        updatedAt:
                          c.updatedAt ||
                          c.createdAt ||
                          new Date().toISOString(),
                        isEdited: c.isEdited,
                      })) || [];

                    return {
                      ...activity,
                      comments: allComments,
                      commentCount: allComments.length,
                      hasMoreComments: false,
                    };
                  }
                ),
              })),
            };
          }
        );
      }
    } catch {
      toast.error(t('common:comments.loadError'));
    } finally {
      setLoadingComments(prev => ({ ...prev, [activityId]: false }));
    }
  };

  // Handle edit comment
  const handleEditComment = async (
    activityId: string,
    commentId: string,
    newText: string
  ) => {
    // Validate comment
    const validation = activityInteractionService.validateComment(newText);
    if (!validation.isValid) {
      return;
    }

    const previousData = queryClient.getQueryData(feedQueryKey);

    queryClient.setQueryData(
      feedQueryKey,
      (oldData: ActivityFeedInfiniteData | undefined) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          pages: oldData.pages.map((page: ActivityFeedPage) => ({
            ...page,
            activities: page.activities.map((activity: ActivityFeedItemDto) => {
              if (activity.id !== activityId) return activity;

              const updatedComments = (activity.comments || []).map(comment => {
                if (comment.id === commentId) {
                  return {
                    ...comment,
                    text: validation.sanitizedText || newText,
                    updatedAt: new Date().toISOString(),
                    isEdited: true,
                  };
                }
                return comment;
              });

              return {
                ...activity,
                comments: updatedComments,
              };
            }),
          })),
        };
      }
    );

    try {
      setActiveExpenseId(activityId);
      await updateCommentMutation.mutateAsync({
        commentId,
        text: validation.sanitizedText || newText,
        expenseId: activityId,
      });
    } catch {
      queryClient.setQueryData(feedQueryKey, previousData);
      toast.error(t('common:comments.editError'));
    }
  };

  // Handle delete comment
  const handleDeleteComment = async (activityId: string, commentId: string) => {
    const previousData = queryClient.getQueryData(feedQueryKey);

    queryClient.setQueryData(
      feedQueryKey,
      (oldData: ActivityFeedInfiniteData | undefined) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          pages: oldData.pages.map((page: ActivityFeedPage) => ({
            ...page,
            activities: page.activities.map((activity: ActivityFeedItemDto) => {
              if (activity.id !== activityId) return activity;

              const updatedComments = (activity.comments || []).filter(
                comment => comment.id !== commentId
              );

              return {
                ...activity,
                comments: updatedComments,
                commentCount: Math.max(0, (activity.commentCount || 0) - 1),
              };
            }),
          })),
        };
      }
    );

    try {
      await api.expenses.deleteComment(commentId, activityId);
    } catch {
      queryClient.setQueryData(feedQueryKey, previousData);
      toast.error(t('common:comments.deleteError'));
    }
  };

  return (
    <div data-testid="activity-feed-container">
      <ActivityFeedPresentation
        activities={activities}
        showSupporterBadges
        initialLoading={initialLoading}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={hasNextPage}
        expandedComments={expandedComments}
        loadingComments={loadingComments}
        loadingReaction={loadingReaction}
        lastActivityRef={lastActivityRef}
        onLoadMore={loadMore}
        showTitle={showTitle}
        filterMode={filterMode}
        onFilterChange={setActivityFilter}
        onAddReaction={handleAddReaction}
        onToggleReaction={handleToggleReaction}
        onSubmitComment={handleSubmitComment}
        onLoadAllComments={handleLoadAllComments}
        onEditComment={handleEditComment}
        onDeleteComment={handleDeleteComment}
      />
    </div>
  );
}
